import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from './icons';
import { nativeSources, type NativeSource } from '../data/sources';
import './NativeSources.css';

export const NativeSources: React.FC = () => {
  return (
    <section id="sources" className="sources" aria-labelledby="sources-title">
      <div className="container">
        <header className="sources__head">
          <span className="eyebrow">Native content · 本物</span>
          <h2 id="sources-title" className="sources__title">
            Real Japanese. From real Japanese sources.
          </h2>
          <p className="sources__lead">
            Vocabulary, kanji, readings and the bulk of our example sentences come from
            community-maintained, openly-licensed datasets used by professional Japanese
            dictionaries and native speakers. Audio is generated by a Japanese-trained
            TTS engine (VOICEVOX) — not native recordings, but far closer to real
            Japanese pronunciation than English-trained voices.
          </p>
        </header>

        <ul className="sources__grid" role="list">
          {nativeSources.map((src, i) => (
            <SourceCard key={src.id} source={src} delay={i * 0.05} />
          ))}
        </ul>

        <aside className="sources__transparency" aria-labelledby="sources-transparency-title">
          <span className="sources__transparency-label mono">One honest caveat</span>
          <h3 id="sources-transparency-title" className="sources__transparency-title">
            A portion of drill sentences is generated by our own algorithm.
          </h3>
          <p className="sources__transparency-body">
            To give every word, conjugation and grammar point enough practice material, we
            supplement native corpora with sentences produced by an in-house generator
            grounded in published research on Japanese morphology and JLPT grammar patterns.
            It lets us guarantee coverage — but it's not perfect.
          </p>
          <p className="sources__transparency-body">
            So yes: occasionally you'll meet a grammatically correct sentence that reads a
            little like Duolingo's <em>"I am an apple"</em> — technically fine, contextually
            odd. Removing those entirely would mean dropping whole vocabulary items from
            practice, which hurts serious learners more than the occasional awkward
            sentence does. Every generated sentence is clearly flagged in-app, and
            native-sourced sentences always take priority when one exists.
          </p>
        </aside>

        <div className="sources__foot">
          <p className="sources__foot-line mono">
            Full attribution, contributor lists and license texts ship inside the app under
            Settings → Credits — exactly as each license requires.
          </p>
        </div>
      </div>
    </section>
  );
};

const SourceCard: React.FC<{ source: NativeSource; delay: number }> = ({ source, delay }) => {
  const prefersReduced = useReducedMotion();
  return (
    <motion.li
      className={`source source--${source.tone}`}
      initial={prefersReduced ? undefined : { opacity: 0, y: 24 }}
      whileInView={prefersReduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="source__top">
        <span className="jp source__jp" aria-hidden>
          {source.jp}
        </span>
        <span className="source__license mono" title={`License: ${source.license}`}>
          {source.license}
        </span>
      </div>

      <div className="source__stat-row">
        <span className="source__stat">{source.stat}</span>
        <span className="source__stat-label mono">{source.statLabel}</span>
      </div>

      <h3 className="source__name">{source.name}</h3>
      <p className="source__role mono">{source.role}</p>
      <p className="source__desc">{source.description}</p>

      <div className="source__meta">
        <span className="source__maintainer">Maintained by {source.maintainer}</span>
      </div>

      <div className="source__links">
        <a
          href={source.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="source__link"
        >
          Visit source
          <ArrowRightIcon size={14} />
        </a>
        <a
          href={source.licenseUrl}
          target="_blank"
          rel="noreferrer"
          className="source__link source__link--sub"
        >
          License
        </a>
      </div>
    </motion.li>
  );
};
